import { useAuth0 } from "@auth0/auth0-react"
import { useEffect, useReducer } from "react"
import { useParams } from "react-router-dom"
import { List } from "semantic-ui-react"
import { ReactComponent as DevicesIcon } from "../../../Icons/devices-icon.svg"
import { deviceStructureReducer } from "../store/reducers"
import { deviceStructureState } from "../store/states"

/**
 * The custom hook for the device structure component.
 * @returns {Object} state - The state of the device structure.
 * @returns {Function} handleSearch - The function to handle the search. It will filter the data based on the search string.
 * @returns {Function} getDeviceStructureByOrgId - The function to fetch the device structure data based on the organization id.
 */
export const useDeviceStructure = () => {
	/**
	 * useAuth0 is a hook provided by the auth0-react SDK that provides the authentication state.
	 * @property {Function} getAccessTokenSilently - The function to get the access token silently.
	 */
	const { getAccessTokenSilently } = useAuth0()

	/**
	 * useParams is a hook that returns an object of key/value pairs of URL parameters.
	 * @property {string} id - The URL id parameter. Used the alias as orgId.
	 */
	const { id: orgId } = useParams()

	/**
	 * useReducer is a hook that is used for state management.
	 * @parameter {Function} deviceStructureReducer - The reducer function for the device structure component.
	 * @parameter {Object} deviceStructureState - The initial state for the device structure component.
	 * @returns {Array} [state, dispatch] - The state of the device structure component and the dispatch function.
	 */
	const [state, dispatch] = useReducer(deviceStructureReducer, deviceStructureState)

	/**
	 * The function to handle the search. It will filter the data based on the search string. And set the search result to the commponent state.
	 * The first dispatch will set the search string.
	 * Then it will check if the search string is empty, it will set the search result to the device data. Otherwise, it will filter the device data based on the search string.
	 * At the end it will set the search result to the filtered data.
	 * @param {Object} e - The event object.
	 */
	const handleSearch = (e) => {
		dispatch({ type: "SET_SEARCH_STRING", payload: e.target.value })
		if (e.target.value.trim() === "") dispatch({ type: "SET_SEARCH_RESULT", payload: state.deviceData })
		else {
			dispatch({ type: "SET_SEARCH_RESULT", payload: state.deviceData.filter((element) => element.name.toLowerCase().trim().includes(e.target.value.toLowerCase().trim())) })
		}
	}

	/**
	 * The function to transform the device data to the device structure component attributes.
	 * @param {Object} data - The device data.
	 * @returns {Array} - The transformed device data.
	 */
	const transformDeviceData = (data) =>
		data.map((element) => ({
			...element,
			name: element.shortName,
			listType: "devices",
			listHandler: <></>, // We can add the remove device button right here. But for now, we will leave it empty.
			icon: (
				<List.Icon color="black">
					<DevicesIcon />
				</List.Icon>
			)
		}))

	/**
	 * The function to fetch the device structure data based on the organization id.
	 * @returns {Promise} - The promise object of the fetch request.
	 */
	const getDeviceStructureByOrgId = async () => {
		dispatch({ type: "SET_IS_LOADING", payload: true })
		dispatch({ type: "SET_IS_SUCCESS", payload: false })
		return fetch(
			new Request(process.env.REACT_APP_SESSION_ADDR + "/api/v2/org/" + orgId + "/device", {
				method: "GET",
				headers: {
					"authorization": "Bearer " + (await getAccessTokenSilently()),
					"Content-Type": "application/json"
				}
			})
		)
			.then((response) => {
				if (response.ok) {
					return response.json()
				} else {
					throw new Error("Failed to fetch devices")
				}
			})
			.then((data) => transformDeviceData(data))
			.then((devices) => {
				dispatch({ type: "SET_IS_SUCCESS", payload: true })
				dispatch({ type: "SET_DEVICES_DATA", payload: devices })
			})
			.catch((error) => {
				dispatch({ type: "SET_IS_ERROR", payload: true })
				dispatch({ type: "SET_ERROR_MESSAGE", payload: error.message })
			})
			.finally(() => {
				dispatch({ type: "SET_IS_LOADING", payload: false })
			})
	}

	/**
	 * The useEffect hook is used to fetch the device structure data based on the organization id.
	 * It will run once when the component is mounted.
	 */
	useEffect(() => {
		getDeviceStructureByOrgId()
	}, [])

	/**
	 * The useEffect hook is used to handle the search. It will filter the data based on the search string.
	 * It will run when the device data is changed. To update the list of devices based on the search string.
	 */
	useEffect(() => {
		handleSearch({ target: { value: state.searchString } })
	}, [state.deviceData])

	return { state, handleSearch, getDeviceStructureByOrgId }
}
